import { useEffect, useRef, useState } from "react";
import { useBalances } from "../state/balances.js";
import { serverNow } from "../state/serverTime.js";
import { useT } from "../i18n/index.js";
import { formatCompact } from "../ui/Counter.js";
import { ResourceIcon } from "../assets/gameIcons.js";

/** The credit counter: live minutes, a +1 pulse every minute and a progress ring to the next credit (R50). */
export function CreditCounter() {
  const t = useT();
  const balances = useBalances((s) => s.balances);
  const [credits, setCredits] = useState(0);
  const [progress, setProgress] = useState(0);
  const [pulse, setPulse] = useState(0);
  const lastRef = useRef<number | null>(null);

  useEffect(() => {
    let raf = 0;
    let last = 0;
    const loop = (ts: number) => {
      raf = requestAnimationFrame(loop);
      if (ts - last < 250) return;
      last = ts;
      const st = useBalances.getState();
      const now = serverNow();
      const live = st.liveCredits(now);
      const ms = st.msToNextCredit(now);
      setProgress(1 - ms / 60_000);
      if (lastRef.current !== null && live > lastRef.current) setPulse((p) => p + 1);
      lastRef.current = live;
      setCredits(live);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const size = 40;
  const r = 16;
  const c = 2 * Math.PI * r;
  return (
    <div className="row glass credit-stock" data-testid="credit-counter" style={{ padding: "4px 12px 4px 6px", gap: 8, borderRadius: 7, borderColor: "rgba(245,197,66,0.35)" }} title={t("hud.credits.next", { s: Math.ceil((1 - progress) * 60) })}>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg data-visualization="credit-progress" aria-hidden="true" width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={3} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--gold)" strokeWidth={3} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - progress)} style={{ transition: "stroke-dashoffset 250ms linear" }} />
        </svg>
        <div key={pulse} style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "var(--gold)", fontWeight: 800, fontSize: 15, borderRadius: "50%", animation: pulse ? "pulse-gold 700ms ease-out" : undefined }}>
          <ResourceIcon resource="CREDITS" size={23} label={t("store.credits")} />
        </div>
        {pulse ? (
          <div key={`p${pulse}`} style={{ position: "absolute", left: "50%", top: -4, transform: "translateX(-50%)", color: "var(--gold)", fontWeight: 700, fontSize: 12, animation: "pop 900ms ease-out both", pointerEvents: "none" }}>
            +1
          </div>
        ) : null}
      </div>
      <span className="mono" style={{ fontWeight: 700, color: "var(--gold)", fontSize: 16, minWidth: 48, textAlign: "right" }} data-testid="credit-value">
        {balances ? formatCompact(credits) : "–"}
      </span>
    </div>
  );
}
